import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiSettings, FiShield, FiMail, FiDatabase, FiLock, FiAlertCircle, FiSave, FiRefreshCw, FiChevronDown } from "react-icons/fi";

export default function SettingsPage() {
	const [activeTab, setActiveTab] = useState("general");
	const [generalSettings, setGeneralSettings] = useState({
		appName: "Sec1 OT Security Platform",
		theme: "dark",
		language: "en",
		timezone: "UTC",
		dateFormat: "YYYY-MM-DD",
		enableNotifications: true,
		notificationSound: true,
		emailNotifications: true,
	});

	const [securitySettings, setSecuritySettings] = useState({
		twoFactorAuth: true,
		sessionTimeout: 30,
		passwordMinLength: 12,
		passwordExpiry: 90,
		enableIPWhitelisting: true,
		enableAuditLogging: true,
		enableEncryption: true,
		encryptionAlgorithm: "AES-256",
	});

	const [integrationSettings, setIntegrationSettings] = useState({
		integrationTimeout: 30,
		retryAttempts: 3,
		retryDelay: 5,
		enableAutoSync: true,
		syncInterval: 5,
		enableHealthChecks: true,
		enableRateLimiting: true,
		maxRequestsPerMinute: 100,
	});

	const [emailSettings, setEmailSettings] = useState({
		smtpServer: "smtp.sec1.io",
		smtpPort: 587,
		senderEmail: "alerts@sec1.io",
		enableTLS: true,
		requireAuthentication: true,
		authUsername: "alerts",
		alertRecipients: "team@sec1.io",
		enableEmailAlerts: true,
	});

	const [dataSettings, setDataSettings] = useState({
		dataRetentionDays: 90,
		autoBackupEnabled: true,
		backupFrequency: "daily",
		backupLocation: "/backups/ot-security",
		maxLogSize: 500,
		enableCompression: true,
		enableDeduplication: true,
		archiveOldData: true,
	});

	const [apiSettings, setApiSettings] = useState({
		apiBaseUrl: "https://api.sec1.io/v1",
		apiKey: "sk_live_****************************",
		apiVersion: "v1",
		enableApiLogging: true,
		rateLimitPerHour: 1000,
		enableApiDocumentation: true,
		enableWebhooks: true,
		webhookRetryAttempts: 3,
	});

	const handleSaveGeneral = () => {
		alert("General settings saved successfully!");
	};

	const handleSaveSecurity = () => {
		alert("Security settings saved successfully!");
	};

	const handleSaveIntegration = () => {
		alert("Integration settings saved successfully!");
	};

	const handleSaveEmail = () => {
		alert("Email settings saved successfully!");
	};

	const handleSaveData = () => {
		alert("Data management settings saved successfully!");
	};

	const handleSaveAPI = () => {
		alert("API settings saved successfully!");
	};

	const testEmailConnection = () => {
		alert("Test email sent to " + emailSettings.senderEmail);
	};

	const testAPIConnection = () => {
		alert("API connection successful!");
	};

	// Helper: Switch row component
	const SwitchRow = ({ label, checked, onChange, disabled = false }) => (
		<div className="d-flex justify-content-between align-items-center p-3 border rounded mb-2" style={{ borderColor: "#e5e7eb" }}>
			<label className="form-check-label fw-semibold mb-0">{label}</label>
			<div className="form-check form-switch">
				<input className="form-check-input" type="checkbox" id={`switch-${label}`} checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} style={{ cursor: disabled ? "not-allowed" : "pointer" }} />
			</div>
		</div>
	);

	// Helper: Form section header
	const FormSection = ({ title, children }) => (
		<div className="mb-4">
			<h6 className="fw-bold text-gray-900 mb-3" style={{ fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", color: "#6b7280" }}>
				{title}
			</h6>
			<div className="d-flex flex-column gap-2">{children}</div>
		</div>
	);

	// Helper: Form group component
	const FormGroup = ({ label, children, required = false }) => (
		<div className="mb-3">
			<label className="form-label fw-semibold mb-2">{label} {required && <span className="text-danger">*</span>}</label>
			{children}
		</div>
	);

	return (
		<>
			<Helmet>
				<title>Settings | Sec1 Software Pvt. Ltd.</title>
			</Helmet>

			{/* Toolbar */}
			<div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0" style={{ background: "linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderBottom: "2px solid #8b5cf6" }}>
				<div id="kt_app_toolbar_container" className="app-container container-fluid d-flex align-items-stretch">
					<div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
						<div className="page-title d-flex flex-column gap-1 me-3 mb-2">
							<h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
								Settings & Configuration
							</h1>
							<p className="text-gray-600 fs-6 mb-0">Manage application settings, security, integrations, and data management</p>
						</div>
					</div>
				</div>
			</div>

			<div id="kt_app_content" className="app-content flex-column-fluid">
				<div id="kt_app_content_container" className="app-container container-fluid">
					{/* Settings Tabs Card */}
					<div className="card" style={{ borderTop: "3px solid #8b5cf6", boxShadow: "0 4px 20px rgba(139, 92, 246, 0.15)" }}>
						<div className="card-body pt-0">
							{/* Tab Navigation */}
							<ul className="nav nav-tabs border-0 pb-3" role="tablist" style={{ borderBottom: "2px solid #e5e7eb" }}>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "general" ? "active" : ""}`} onClick={() => setActiveTab("general")} style={{ color: activeTab === "general" ? "#3b82f6" : "#6b7280", borderBottom: activeTab === "general" ? "3px solid #3b82f6" : "none" }}>
										<FiSettings size={16} />
										General
									</button>
								</li>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "security" ? "active" : ""}`} onClick={() => setActiveTab("security")} style={{ color: activeTab === "security" ? "#ef4444" : "#6b7280", borderBottom: activeTab === "security" ? "3px solid #ef4444" : "none" }}>
										<FiShield size={16} />
										Security
									</button>
								</li>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "integration" ? "active" : ""}`} onClick={() => setActiveTab("integration")} style={{ color: activeTab === "integration" ? "#3b82f6" : "#6b7280", borderBottom: activeTab === "integration" ? "3px solid #3b82f6" : "none" }}>
										<FiRefreshCw size={16} />
										Integration
									</button>
								</li>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "email" ? "active" : ""}`} onClick={() => setActiveTab("email")} style={{ color: activeTab === "email" ? "#fb923c" : "#6b7280", borderBottom: activeTab === "email" ? "3px solid #fb923c" : "none" }}>
										<FiMail size={16} />
										Email
									</button>
								</li>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "data" ? "active" : ""}`} onClick={() => setActiveTab("data")} style={{ color: activeTab === "data" ? "#14b8a6" : "#6b7280", borderBottom: activeTab === "data" ? "3px solid #14b8a6" : "none" }}>
										<FiDatabase size={16} />
										Data
									</button>
								</li>
								<li className="nav-item" role="presentation">
									<button className={`nav-link d-flex align-items-center gap-2 fw-semibold ${activeTab === "api" ? "active" : ""}`} onClick={() => setActiveTab("api")} style={{ color: activeTab === "api" ? "#8b5cf6" : "#6b7280", borderBottom: activeTab === "api" ? "3px solid #8b5cf6" : "none" }}>
										<FiLock size={16} />
										API
									</button>
								</li>
							</ul>

							{/* Tab Content */}
							<div className="tab-content pt-5">
								{/* General Tab */}
								{activeTab === "general" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<FormSection title="Application">
												<FormGroup label="Application Name" required>
													<input type="text" className="form-control" value={generalSettings.appName} onChange={(e) => setGeneralSettings({ ...generalSettings, appName: e.target.value })} placeholder="Application name" />
												</FormGroup>
												<FormGroup label="Theme" required>
													<select className="form-select" value={generalSettings.theme} onChange={(e) => setGeneralSettings({ ...generalSettings, theme: e.target.value })}>
														<option value="dark">Dark Theme</option>
														<option value="light">Light Theme</option>
														<option value="auto">Auto (System)</option>
													</select>
												</FormGroup>
											</FormSection>

											<FormSection title="Localization">
												<FormGroup label="Language" required>
													<select className="form-select" value={generalSettings.language} onChange={(e) => setGeneralSettings({ ...generalSettings, language: e.target.value })}>
														<option value="en">English</option>
														<option value="hi">Hindi</option>
														<option value="es">Spanish</option>
														<option value="fr">French</option>
													</select>
												</FormGroup>
												<FormGroup label="Timezone" required>
													<select className="form-select" value={generalSettings.timezone} onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}>
														<option value="UTC">UTC</option>
														<option value="IST">IST (India Standard Time)</option>
														<option value="PST">PST (Pacific Standard Time)</option>
														<option value="EST">EST (Eastern Standard Time)</option>
													</select>
												</FormGroup>
												<FormGroup label="Date Format" required>
													<select className="form-select" value={generalSettings.dateFormat} onChange={(e) => setGeneralSettings({ ...generalSettings, dateFormat: e.target.value })}>
														<option value="YYYY-MM-DD">YYYY-MM-DD</option>
														<option value="DD-MM-YYYY">DD-MM-YYYY</option>
														<option value="MM/DD/YYYY">MM/DD/YYYY</option>
													</select>
												</FormGroup>
											</FormSection>

											<FormSection title="Notifications">
												<SwitchRow label="Enable Notifications" checked={generalSettings.enableNotifications} onChange={(checked) => setGeneralSettings({ ...generalSettings, enableNotifications: checked })} />
												<SwitchRow label="Notification Sound" checked={generalSettings.notificationSound} onChange={(checked) => setGeneralSettings({ ...generalSettings, notificationSound: checked })} disabled={!generalSettings.enableNotifications} />
												<SwitchRow label="Email Notifications" checked={generalSettings.emailNotifications} onChange={(checked) => setGeneralSettings({ ...generalSettings, emailNotifications: checked })} disabled={!generalSettings.enableNotifications} />
											</FormSection>

											<button type="button" className="btn btn-primary fw-bold mt-4" onClick={handleSaveGeneral}>
												<FiSave size={16} className="me-2" />
												Save General Settings
											</button>
										</div>
									</div>
								)}

								{/* Security Tab */}
								{activeTab === "security" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<FormSection title="Password Policy">
												<FormGroup label="Minimum Password Length" required>
													<select className="form-select" value={securitySettings.passwordMinLength} onChange={(e) => setSecuritySettings({ ...securitySettings, passwordMinLength: parseInt(e.target.value) })}>
														<option value={8}>8 characters</option>
														<option value={10}>10 characters</option>
														<option value={12}>12 characters</option>
														<option value={16}>16 characters</option>
													</select>
												</FormGroup>
												<FormGroup label="Password Expiry Days" required>
													<input type="number" className="form-control" value={securitySettings.passwordExpiry} onChange={(e) => setSecuritySettings({ ...securitySettings, passwordExpiry: e.target.value })} min="0" />
												</FormGroup>
											</FormSection>

											<FormSection title="Session Management">
												<FormGroup label="Session Timeout (minutes)" required>
													<input type="number" className="form-control" value={securitySettings.sessionTimeout} onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: e.target.value })} min="5" />
												</FormGroup>
											</FormSection>

											<FormSection title="Security Features">
												<SwitchRow label="Two-Factor Authentication" checked={securitySettings.twoFactorAuth} onChange={(checked) => setSecuritySettings({ ...securitySettings, twoFactorAuth: checked })} />
												<SwitchRow label="IP Whitelisting" checked={securitySettings.enableIPWhitelisting} onChange={(checked) => setSecuritySettings({ ...securitySettings, enableIPWhitelisting: checked })} />
												<SwitchRow label="Audit Logging" checked={securitySettings.enableAuditLogging} onChange={(checked) => setSecuritySettings({ ...securitySettings, enableAuditLogging: checked })} />
												<SwitchRow label="Data Encryption" checked={securitySettings.enableEncryption} onChange={(checked) => setSecuritySettings({ ...securitySettings, enableEncryption: checked })} />
												{securitySettings.enableEncryption && (
													<FormGroup label="Encryption Algorithm" required className="mt-3">
														<select className="form-select" value={securitySettings.encryptionAlgorithm} onChange={(e) => setSecuritySettings({ ...securitySettings, encryptionAlgorithm: e.target.value })}>
															<option value="AES-256">AES-256</option>
															<option value="AES-192">AES-192</option>
															<option value="AES-128">AES-128</option>
														</select>
													</FormGroup>
												)}
											</FormSection>

											<button type="button" className="btn btn-primary fw-bold mt-4" onClick={handleSaveSecurity}>
												<FiSave size={16} className="me-2" />
												Save Security Settings
											</button>
										</div>
									</div>
								)}

								{/* Integration Tab */}
								{activeTab === "integration" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<FormSection title="Timeout Configuration">
												<FormGroup label="Integration Timeout (seconds)" required>
													<input type="number" className="form-control" value={integrationSettings.integrationTimeout} onChange={(e) => setIntegrationSettings({ ...integrationSettings, integrationTimeout: e.target.value })} min="5" />
												</FormGroup>
											</FormSection>

											<FormSection title="Retry Policy">
												<FormGroup label="Retry Attempts" required>
													<input type="number" className="form-control" value={integrationSettings.retryAttempts} onChange={(e) => setIntegrationSettings({ ...integrationSettings, retryAttempts: e.target.value })} min="1" />
												</FormGroup>
												<FormGroup label="Retry Delay (seconds)" required>
													<input type="number" className="form-control" value={integrationSettings.retryDelay} onChange={(e) => setIntegrationSettings({ ...integrationSettings, retryDelay: e.target.value })} min="1" />
												</FormGroup>
											</FormSection>

											<FormSection title="Synchronization">
												<SwitchRow label="Auto Sync" checked={integrationSettings.enableAutoSync} onChange={(checked) => setIntegrationSettings({ ...integrationSettings, enableAutoSync: checked })} />
												{integrationSettings.enableAutoSync && (
													<FormGroup label="Sync Interval (minutes)" required className="mt-3">
														<input type="number" className="form-control" value={integrationSettings.syncInterval} onChange={(e) => setIntegrationSettings({ ...integrationSettings, syncInterval: e.target.value })} min="1" />
													</FormGroup>
												)}
											</FormSection>

											<FormSection title="Health & Rate Limiting">
												<SwitchRow label="Health Checks" checked={integrationSettings.enableHealthChecks} onChange={(checked) => setIntegrationSettings({ ...integrationSettings, enableHealthChecks: checked })} />
												<SwitchRow label="Rate Limiting" checked={integrationSettings.enableRateLimiting} onChange={(checked) => setIntegrationSettings({ ...integrationSettings, enableRateLimiting: checked })} />
												{integrationSettings.enableRateLimiting && (
													<FormGroup label="Max Requests Per Minute" required className="mt-3">
														<input type="number" className="form-control" value={integrationSettings.maxRequestsPerMinute} onChange={(e) => setIntegrationSettings({ ...integrationSettings, maxRequestsPerMinute: e.target.value })} min="10" />
													</FormGroup>
												)}
											</FormSection>

											<button type="button" className="btn btn-primary fw-bold mt-4" onClick={handleSaveIntegration}>
												<FiSave size={16} className="me-2" />
												Save Integration Settings
											</button>
										</div>
									</div>
								)}

								{/* Email Tab */}
								{activeTab === "email" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<FormSection title="SMTP Configuration">
												<FormGroup label="SMTP Server" required>
													<input type="text" className="form-control" value={emailSettings.smtpServer} onChange={(e) => setEmailSettings({ ...emailSettings, smtpServer: e.target.value })} placeholder="smtp.example.com" />
												</FormGroup>
												<FormGroup label="SMTP Port" required>
													<input type="number" className="form-control" value={emailSettings.smtpPort} onChange={(e) => setEmailSettings({ ...emailSettings, smtpPort: e.target.value })} />
												</FormGroup>
											</FormSection>

											<FormSection title="Sender Configuration">
												<FormGroup label="Sender Email" required>
													<input type="email" className="form-control" value={emailSettings.senderEmail} onChange={(e) => setEmailSettings({ ...emailSettings, senderEmail: e.target.value })} placeholder="alerts@example.com" />
												</FormGroup>
											</FormSection>

											<FormSection title="Authentication">
												<SwitchRow label="Require Authentication" checked={emailSettings.requireAuthentication} onChange={(checked) => setEmailSettings({ ...emailSettings, requireAuthentication: checked })} />
												<SwitchRow label="Enable TLS" checked={emailSettings.enableTLS} onChange={(checked) => setEmailSettings({ ...emailSettings, enableTLS: checked })} />
												{emailSettings.requireAuthentication && (
													<FormGroup label="Username" required className="mt-3">
														<input type="text" className="form-control" value={emailSettings.authUsername} onChange={(e) => setEmailSettings({ ...emailSettings, authUsername: e.target.value })} placeholder="Username" />
													</FormGroup>
												)}
											</FormSection>

											<FormSection title="Recipients">
												<FormGroup label="Alert Recipients" required>
													<input type="email" className="form-control" value={emailSettings.alertRecipients} onChange={(e) => setEmailSettings({ ...emailSettings, alertRecipients: e.target.value })} placeholder="team@example.com" />
												</FormGroup>
											</FormSection>

											<FormSection title="Alerts">
												<SwitchRow label="Enable Email Alerts" checked={emailSettings.enableEmailAlerts} onChange={(checked) => setEmailSettings({ ...emailSettings, enableEmailAlerts: checked })} />
											</FormSection>

											<div className="d-flex gap-2 mt-4">
												<button type="button" className="btn btn-primary fw-bold" onClick={handleSaveEmail}>
													<FiSave size={16} className="me-2" />
													Save Email Settings
												</button>
												<button type="button" className="btn btn-light fw-bold" onClick={testEmailConnection}>
													<FiRefreshCw size={16} className="me-2" />
													Test Connection
												</button>
											</div>
										</div>
									</div>
								)}

								{/* Data Tab */}
								{activeTab === "data" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<FormSection title="Retention Policy">
												<FormGroup label="Retention Period (days)" required>
													<input type="number" className="form-control" value={dataSettings.dataRetentionDays} onChange={(e) => setDataSettings({ ...dataSettings, dataRetentionDays: e.target.value })} min="30" />
												</FormGroup>
											</FormSection>

											<FormSection title="Backup Configuration">
												<SwitchRow label="Auto Backup" checked={dataSettings.autoBackupEnabled} onChange={(checked) => setDataSettings({ ...dataSettings, autoBackupEnabled: checked })} />
												{dataSettings.autoBackupEnabled && (
													<>
														<FormGroup label="Backup Frequency" required className="mt-3">
															<select className="form-select" value={dataSettings.backupFrequency} onChange={(e) => setDataSettings({ ...dataSettings, backupFrequency: e.target.value })}>
																<option value="hourly">Hourly</option>
																<option value="daily">Daily</option>
																<option value="weekly">Weekly</option>
																<option value="monthly">Monthly</option>
															</select>
														</FormGroup>
														<FormGroup label="Backup Location" required>
															<input type="text" className="form-control" value={dataSettings.backupLocation} onChange={(e) => setDataSettings({ ...dataSettings, backupLocation: e.target.value })} placeholder="/backups" />
														</FormGroup>
													</>
												)}
											</FormSection>

											<FormSection title="Log Management">
												<FormGroup label="Max Log Size (MB)" required>
													<input type="number" className="form-control" value={dataSettings.maxLogSize} onChange={(e) => setDataSettings({ ...dataSettings, maxLogSize: e.target.value })} min="100" />
												</FormGroup>
												<SwitchRow label="Enable Compression" checked={dataSettings.enableCompression} onChange={(checked) => setDataSettings({ ...dataSettings, enableCompression: checked })} />
												<SwitchRow label="Enable Deduplication" checked={dataSettings.enableDeduplication} onChange={(checked) => setDataSettings({ ...dataSettings, enableDeduplication: checked })} />
												<SwitchRow label="Archive Old Data" checked={dataSettings.archiveOldData} onChange={(checked) => setDataSettings({ ...dataSettings, archiveOldData: checked })} />
											</FormSection>

											<button type="button" className="btn btn-primary fw-bold mt-4" onClick={handleSaveData}>
												<FiSave size={16} className="me-2" />
												Save Data Settings
											</button>
										</div>
									</div>
								)}

								{/* API Tab */}
								{activeTab === "api" && (
									<div className="row">
										<div className="col-12 col-lg-8">
											<div className="p-4 border rounded mb-4" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #ef4444", boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08)" }}>
												<div className="d-flex gap-2 align-items-start">
													<FiAlertCircle size={20} color="#ef4444" className="mt-1" style={{ flexShrink: 0 }} />
													<div>
														<p className="fw-semibold text-gray-900 mb-1">API Security Notice</p>
														<p className="text-gray-600 fs-6 mb-0">Keep API keys confidential. Never share them publicly. Rotate keys regularly for security.</p>
													</div>
												</div>
											</div>

											<FormSection title="Endpoint Configuration">
												<FormGroup label="API Base URL" required>
													<input type="text" className="form-control" value={apiSettings.apiBaseUrl} onChange={(e) => setApiSettings({ ...apiSettings, apiBaseUrl: e.target.value })} placeholder="https://api.example.com/v1" />
												</FormGroup>
												<FormGroup label="API Version" required>
													<select className="form-select" value={apiSettings.apiVersion} onChange={(e) => setApiSettings({ ...apiSettings, apiVersion: e.target.value })}>
														<option value="v1">v1</option>
														<option value="v2">v2</option>
														<option value="v3">v3</option>
													</select>
												</FormGroup>
											</FormSection>

											<FormSection title="Authentication">
												<FormGroup label="API Key" required>
													<input type="password" className="form-control" value={apiSettings.apiKey} onChange={(e) => setApiSettings({ ...apiSettings, apiKey: e.target.value })} placeholder="sk_live_****" />
												</FormGroup>
											</FormSection>

											<FormSection title="Rate Limiting">
												<FormGroup label="Rate Limit Per Hour" required>
													<input type="number" className="form-control" value={apiSettings.rateLimitPerHour} onChange={(e) => setApiSettings({ ...apiSettings, rateLimitPerHour: e.target.value })} min="100" />
												</FormGroup>
											</FormSection>

											<FormSection title="API Features">
												<SwitchRow label="API Logging" checked={apiSettings.enableApiLogging} onChange={(checked) => setApiSettings({ ...apiSettings, enableApiLogging: checked })} />
												<SwitchRow label="API Documentation" checked={apiSettings.enableApiDocumentation} onChange={(checked) => setApiSettings({ ...apiSettings, enableApiDocumentation: checked })} />
												<SwitchRow label="Webhooks" checked={apiSettings.enableWebhooks} onChange={(checked) => setApiSettings({ ...apiSettings, enableWebhooks: checked })} />
												{apiSettings.enableWebhooks && (
													<FormGroup label="Webhook Retry Attempts" required className="mt-3">
														<input type="number" className="form-control" value={apiSettings.webhookRetryAttempts} onChange={(e) => setApiSettings({ ...apiSettings, webhookRetryAttempts: e.target.value })} min="1" />
													</FormGroup>
												)}
											</FormSection>

											<div className="d-flex gap-2 mt-4">
												<button type="button" className="btn btn-primary fw-bold" onClick={handleSaveAPI}>
													<FiSave size={16} className="me-2" />
													Save API Settings
												</button>
												<button type="button" className="btn btn-light fw-bold" onClick={testAPIConnection}>
													<FiRefreshCw size={16} className="me-2" />
													Test Connection
												</button>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
